<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-3 flex flex-col gap-4 border-b border-slate-100 bg-white">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">HR</h2>
      </div>

      <div class="relative z-0 px-4 sm:px-6 py-4 min-w-0">
        <div class="bg-white shadow-sm border border-slate-200 overflow-hidden min-w-0">
          <div class="px-4 sm:px-6 pt-2 pb-1">
            <div class="flex items-baseline gap-2 flex-wrap">
              <h2 class="text-base font-bold text-[#0d4169] text-left">
                Find Employee
              </h2>
            </div>
          </div>

          <form class="px-4 sm:px-6 pt-2" @submit.prevent="findEmployees">
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 items-end gap-3 min-w-0">
              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find by ID</label>
                <input
                  v-model.trim="filters.id"
                  type="text"
                  inputmode="numeric"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee ID"
                >
              </div>

              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find by Name</label>
                <input
                  v-model.trim="filters.name"
                  type="text"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee name"
                >
              </div>

              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find By Phone</label>
                <input
                  v-model.trim="filters.phone"
                  type="text"
                  inputmode="tel"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee phone"
                >
              </div>

              <div class="flex justify-stretch sm:justify-start min-w-0">
                <button
                  type="submit"
                  :disabled="loading || !canFindEmployee"
                  class="w-full sm:w-auto min-w-max px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <div
                    v-if="loading"
                    class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
                  ></div>
                  <span v-if="!loading">Find</span>
                </button>
              </div>
            </div>
            <p v-if="errorMessage" class="mt-2 text-xs text-red-600 text-left">{{ errorMessage }}</p>
          </form>

          <div class="px-4 sm:px-6 pb-6 pt-4 min-w-0">
            <div
              class="w-full max-w-full overscroll-x-contain"
              :class="loading ? 'overflow-x-hidden' : 'overflow-x-auto'"
            >
              <table class="w-full min-w-[980px] border-collapse bg-white rounded-xl shadow-sm">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th @click="sortBy('full_name')" class="sort-col text-[12px]">
                      Full Name <span v-if="sortKey === 'full_name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('phone_number')" class="sort-col text-[12px]">
                      Phone <span v-if="sortKey === 'phone_number'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('role')" class="sort-col text-[12px]">
                      Role <span v-if="sortKey === 'role'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('departments')" class="sort-col text-[12px]">
                      Department <span v-if="sortKey === 'departments'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('username')" class="sort-col text-[12px]">
                      Username <span v-if="sortKey === 'username'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('salary')" class="sort-col text-[12px]">
                      Salary <span v-if="sortKey === 'salary'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('created_at')" class="sort-col text-[12px]">
                      Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('status')" class="sort-col text-[12px]">
                      Status <span v-if="sortKey === 'status'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th class="text-[12px]">Actions</th>
                  </tr>
                </thead>
                <tbody class="text-[12px]">
                  <tr
                    v-for="(user, index) in sortedUsers"
                    v-show="!loading"
                    :key="user.id"
                    class="hover:bg-slate-100/50 transition-colors border-b"
                  >
                    <td class="font-medium text-slate-500">{{ index + 1 }}</td>
                    <td class="font-bold text-[#0d4169]">{{ user.full_name }}</td>
                    <td>{{ user.phone_number }}</td>
                    <td>
                      <span
                        v-if="user.role"
                        class="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600 border border-slate-200 uppercase tracking-tighter whitespace-nowrap inline-block"
                      >
                        {{ user.role.split(',')[0].trim() }}{{ (user.role && user.role.split(',').length > 1) ? ' +' + (user.role.split(',').length - 1) : '' }}
                      </span>
                    </td>
                    <td>
                      <span
                        v-if="user.departments"
                        class="px-2 py-0.5 bg-indigo-50 rounded text-[10px] font-bold text-indigo-600 border border-indigo-200 uppercase tracking-tighter whitespace-nowrap inline-block"
                      >
                        {{ user.departments.split(',')[0].trim() }}{{ (user.departments && user.departments.split(',').length > 1) ? ' +' + (user.departments.split(',').length - 1) : '' }}
                      </span>
                    </td>
                    <td class="text-slate-500 font-medium">{{ user.username }}</td>
                    <td class="text-slate-600 font-medium">{{ formatSalary(user.salary) }}</td>
                    <td class="text-slate-400 italic">{{ formatDate(user.created_at) }}</td>
                    <td>
                      <span class="status-pill" :class="user.status === 1 ? 'active' : 'inactive'">
                        {{ user.status === 1 ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="actions relative">
                      <button
                        type="button"
                        class="menu-btn"
                        title="Actions"
                        @click.stop="toggleActionMenu(user.id, $event)"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="loading">
                    <td colspan="10" class="text-center py-10">
                      <div class="flex items-center justify-center">
                        <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="hasSearched && sortedUsers.length === 0">
                    <td colspan="10" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                      No employees found.
                    </td>
                  </tr>
                  <tr v-else-if="!hasSearched">
                    <td colspan="10" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                      Enter ID, name, and/or phone, then click Find.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="activeMenuUser"
      ref="menuPanelRef"
      class="employee-action-menu-panel action-menu action-menu-animate bg-white border border-slate-200 shadow-xl rounded-md p-2"
      :style="menuStyle"
      @click.stop
    >
      <button type="button" class="action-menu-item action-menu-item-edit" :disabled="!canEditEmployee" @click="onEdit">
        <svg class="w-4 h-4 shrink-0 text-[#2563eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        Edit
      </button>
      <button type="button" class="action-menu-item" :disabled="!canPrintEmployee" @click="onPrintId">
        <svg class="w-4 h-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        Print ID
      </button>
    </div>
  </Teleport>

  <EmployeeEditModal
    :is-open="showEditModal"
    :employee="editingEmployee"
    @close="closeEditModal"
    @save="onEmployeeSaved"
  />
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import { jsPDF } from 'jspdf'
import QRCode from 'qrcode'
import { apiFetch, resolveCompanyLogoUrl } from '../../../utils/api'
import EmployeeEditModal from './EmployeeEditModal.vue'

const PRIVILEGE_NAME = 'HR Find Employee'
const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]
  return false
}

function hasEmployeePrivilege(action) {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), action)
}

const canFindEmployee = computed(() => hasEmployeePrivilege('Find'))
const canEditEmployee = computed(() => hasEmployeePrivilege('Edit'))
const canPrintEmployee = computed(() => hasEmployeePrivilege('Print ID'))

const filters = ref({
  id: '',
  name: '',
  phone: '',
})

const users = ref([])
const loading = ref(false)
const hasSearched = ref(false)
const errorMessage = ref('')
const sortKey = ref('full_name')
const sortOrder = ref('asc')
const openActionMenuId = ref(null)
const menuStyle = ref({})
const menuPanelRef = ref(null)
let menuTriggerEl = null
const showEditModal = ref(false)
const editingEmployee = ref(null)

const sortedUsers = computed(() => {
  const result = [...users.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

const activeMenuUser = computed(() => {
  if (!openActionMenuId.value) return null
  return users.value.find((user) => user.id === openActionMenuId.value) || null
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function formatSalary(value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function updateMenuPosition(buttonEl) {
  const rect = buttonEl.getBoundingClientRect()
  const menuWidth = 176
  const edgeGap = 8
  const gap = 4
  let left = rect.right - menuWidth
  if (left < edgeGap) left = Math.max(edgeGap, rect.left)
  if (left + menuWidth > window.innerWidth - edgeGap) {
    left = Math.max(edgeGap, window.innerWidth - menuWidth - edgeGap)
  }

  const menuHeight = menuPanelRef.value?.offsetHeight || 180
  const spaceBelow = window.innerHeight - rect.bottom - edgeGap
  const spaceAbove = rect.top - edgeGap
  const openUp = spaceBelow < menuHeight && spaceAbove >= spaceBelow

  if (openUp) {
    menuStyle.value = {
      left: `${left}px`,
      bottom: `${window.innerHeight - rect.top + gap}px`,
      top: 'auto',
      maxHeight: `${Math.max(120, spaceAbove - gap)}px`,
      overflowY: 'auto',
    }
  } else {
    menuStyle.value = {
      left: `${left}px`,
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
      maxHeight: `${Math.max(120, spaceBelow - gap)}px`,
      overflowY: 'auto',
    }
  }
}

function repositionMenu() {
  if (!menuTriggerEl) return
  updateMenuPosition(menuTriggerEl)
  requestAnimationFrame(() => {
    if (menuTriggerEl) updateMenuPosition(menuTriggerEl)
  })
}

function toggleActionMenu(userId, event) {
  if (openActionMenuId.value === userId) {
    openActionMenuId.value = null
    menuTriggerEl = null
    return
  }

  openActionMenuId.value = userId
  menuTriggerEl = event.currentTarget
  nextTick(() => {
    repositionMenu()
  })
}

function closeActionMenu(event) {
  if (
    event?.target?.closest?.('.employee-action-menu-panel') ||
    event?.target?.closest?.('.menu-btn')
  ) {
    return
  }
  openActionMenuId.value = null
  menuTriggerEl = null
}

function handleMenuReposition() {
  if (openActionMenuId.value && menuTriggerEl) {
    repositionMenu()
  }
}

function openEmployeeModal() {
  editingEmployee.value = activeMenuUser.value
  showEditModal.value = true
  openActionMenuId.value = null
  menuTriggerEl = null
}

function onEdit() {
  if (!canEditEmployee.value) return
  openEmployeeModal()
}

function closeEditModal() {
  showEditModal.value = false
  editingEmployee.value = null
}

function onEmployeeSaved(saved) {
  if (!saved || saved.id == null) return
  const idx = users.value.findIndex((u) => Number(u.id) === Number(saved.id))
  if (idx < 0) return
  const current = users.value[idx]
  users.value[idx] = {
    ...current,
    full_name: saved.full_name != null ? saved.full_name : current.full_name,
    phone_number: saved.phone_number != null ? saved.phone_number : current.phone_number,
    sex: saved.sex != null ? saved.sex : current.sex,
    salary:
      saved.salary !== undefined && saved.salary !== null
        ? saved.salary
        : current.salary,
  }
}

const CARD_PRIMARY_RGB = [13, 65, 105]
/** Standard CR80 ID card (landscape): 85.60mm × 53.98mm */
const ID_CARD_W = 85.6
const ID_CARD_H = 53.98

const printOrgName = computed(() => {
  const user = currentUser.value || {}
  const hasBranch = !!(user.branchId || user.branch_id)
  if (hasBranch) {
    return user.branch?.name || user.branchName || user.branch_name || 'Branch'
  }
  return user.company?.name || user.companyName || user.company_name || 'Company'
})

const printOrgLogo = computed(() => {
  const user = currentUser.value || {}
  const hasBranch = !!(user.branchId || user.branch_id)
  if (hasBranch) {
    const branchLogo =
      user.branchLogo ||
      user.branch_logo ||
      user.branch?.logo_url ||
      user.branch?.logoUrl ||
      ''
    if (branchLogo) return resolveCompanyLogoUrl(branchLogo)
  }
  const companyLogo =
    user.companyLogo ||
    user.company_logo ||
    user.company?.logo_url ||
    user.company?.logoUrl ||
    user.logo ||
    ''
  return resolveCompanyLogoUrl(companyLogo)
})

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl),
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

function formatEmployeeRoles(role) {
  if (!role) return ''
  return String(role)
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean)
    .join(', ')
}

function formatEmployeeIdCode(id) {
  return `0000${id}`
}

function buildEmployeeQrPayload({ orgName, fullName, phone, roles, empId }) {
  return [
    `Company/Branch Name: ${orgName}`,
    `Full Name: ${fullName}`,
    `Phone: ${phone}`,
    `Role: ${roles}`,
    `ID: ${empId}`,
  ].join('\n')
}

async function buildEmployeeQrDataUrl(payload) {
  try {
    return await QRCode.toDataURL(String(payload || ''), {
      margin: 1,
      width: 512,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' },
    })
  } catch {
    return null
  }
}

function drawQrCornerFrame(doc, x, y, size, inset = 1.2, arm = 3.2) {
  const outer = size + inset * 2
  const ox = x - inset
  const oy = y - inset
  doc.setDrawColor(...CARD_PRIMARY_RGB)
  doc.setLineWidth(0.45)
  // Top-left
  doc.line(ox, oy, ox + arm, oy)
  doc.line(ox, oy, ox, oy + arm)
  // Top-right
  doc.line(ox + outer, oy, ox + outer - arm, oy)
  doc.line(ox + outer, oy, ox + outer, oy + arm)
  // Bottom-left
  doc.line(ox, oy + outer, ox + arm, oy + outer)
  doc.line(ox, oy + outer, ox, oy + outer - arm)
  // Bottom-right
  doc.line(ox + outer, oy + outer, ox + outer - arm, oy + outer)
  doc.line(ox + outer, oy + outer, ox + outer, oy + outer - arm)
}

function drawDefaultUserIcon(doc, x, y, size) {
  const cx = x + size / 2
  const cy = y + size / 2
  doc.setFillColor(230, 236, 242)
  doc.rect(x, y, size, size, 'F')
  doc.setFillColor(...CARD_PRIMARY_RGB)
  const headR = size * 0.16
  doc.circle(cx, cy - size * 0.12, headR, 'F')
  doc.setFillColor(...CARD_PRIMARY_RGB)
  doc.ellipse(cx, cy + size * 0.38, size * 0.28, size * 0.22, 'F')
  doc.setFillColor(230, 236, 242)
  doc.rect(x, y + size * 0.78, size, size * 0.3, 'F')
}

async function generateEmployeeIdCardPdf(employee) {
  if (!employee) return

  const fullName = String(employee.full_name || '').trim() || 'Employee'
  const roles = formatEmployeeRoles(employee.role) || '—'
  const empId = employee.id
  const idCode = formatEmployeeIdCode(empId)
  const phone = String(employee.phone_number || '').trim() || '—'
  const orgName = String(printOrgName.value || 'Company')
  const orgLogoUrl = String(printOrgLogo.value || '').trim()
  const photoUrl = resolveCompanyLogoUrl(employee.photo || '')

  const doc = new jsPDF({
    unit: 'mm',
    format: [ID_CARD_W, ID_CARD_H],
    orientation: 'landscape',
  })
  const W = ID_CARD_W
  const H = ID_CARD_H

  doc.setFillColor(255, 255, 255)
  doc.rect(0, 0, W, H, 'F')

  // Top primary header band (landscape)
  const headerH = H * 0.34
  doc.setFillColor(...CARD_PRIMARY_RGB)
  doc.rect(0, 0, W, headerH, 'F')

  // Decorative circles (right side of header edge)
  doc.setFillColor(255, 255, 255)
  doc.circle(W - 7.5, headerH + 0.8, 2.8, 'F')
  doc.setFillColor(234, 179, 8)
  doc.circle(W - 5.2, headerH + 3.8, 1.4, 'F')
  doc.setFillColor(255, 255, 255)
  doc.circle(W - 9.6, headerH + 4.8, 1.0, 'F')

  // Org logo + name (top-right area of header)
  const logoSize = 7.5
  const logoX = W - logoSize - 4
  const logoY = 3
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(orgLogoUrl)
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, logoFormat, logoX, logoY, logoSize, logoSize, undefined, 'FAST')
    } catch {
      // skip logo if image cannot be rendered
    }
  }

  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  const orgMaxW = logoX - 8
  const orgLines = doc.splitTextToSize(orgName, orgMaxW)
  doc.text(orgLines.slice(0, 2), 4, logoY + 3.2)

  // Employee photo (left, overlapping header/body)
  const photoSize = 22
  const photoX = 5
  const photoY = headerH - photoSize * 0.42
  doc.setDrawColor(...CARD_PRIMARY_RGB)
  doc.setLineWidth(0.55)
  doc.setFillColor(255, 255, 255)
  doc.rect(photoX - 1.1, photoY - 1.1, photoSize + 2.2, photoSize + 2.2, 'FD')
  doc.setDrawColor(255, 255, 255)
  doc.setLineWidth(0.4)
  doc.rect(photoX - 0.45, photoY - 0.45, photoSize + 0.9, photoSize + 0.9, 'S')

  const { dataUrl: photoDataUrl, format: photoFormat } = await imageUrlToDataUrl(photoUrl)
  if (photoDataUrl) {
    try {
      doc.addImage(photoDataUrl, photoFormat, photoX, photoY, photoSize, photoSize, undefined, 'FAST')
    } catch {
      drawDefaultUserIcon(doc, photoX, photoY, photoSize)
    }
  } else {
    drawDefaultUserIcon(doc, photoX, photoY, photoSize)
  }

  // Authorize signature under photo
  const sigLineY = photoY + photoSize + 4.2
  const sigLineX1 = photoX + 1
  const sigLineX2 = photoX + photoSize - 1
  doc.setDrawColor(120, 120, 120)
  doc.setLineWidth(0.25)
  doc.line(sigLineX1, sigLineY, sigLineX2, sigLineY)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(5.5)
  doc.setTextColor(90, 90, 90)
  doc.text('Authorize Signature', (sigLineX1 + sigLineX2) / 2, sigLineY + 2.6, { align: 'center' })

  // Text block (center-right of card)
  const textLeft = photoX + photoSize + 5
  const qrSize = 18
  const qrX = W - qrSize - 5.5
  const textMaxW = qrX - textLeft - 3.5
  let textY = headerH + 5.5

  doc.setTextColor(20, 20, 20)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  const nameLines = doc.splitTextToSize(fullName, textMaxW)
  doc.text(nameLines.slice(0, 2), textLeft, textY)
  textY += 4.0 * Math.min(nameLines.length, 2) + 0.6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.setTextColor(55, 55, 55)
  const roleLines = doc.splitTextToSize(roles, textMaxW)
  doc.text(roleLines.slice(0, 2), textLeft, textY)
  textY += 3.2 * Math.min(roleLines.length, 2) + 2.4

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.2)
  doc.setTextColor(30, 30, 30)
  doc.text(`ID No : ${idCode}`, textLeft, textY)
  textY += 3.6
  doc.setTextColor(...CARD_PRIMARY_RGB)
  doc.text(`Phone : ${phone}`, textLeft, textY)

  // QR code (bottom-right) with corner frame
  const qrPayload = buildEmployeeQrPayload({
    orgName,
    fullName,
    phone,
    roles,
    empId,
  })
  const qrDataUrl = await buildEmployeeQrDataUrl(qrPayload)
  const qrY = H - qrSize - 5
  if (qrDataUrl) {
    try {
      drawQrCornerFrame(doc, qrX, qrY, qrSize)
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'FAST')
    } catch {
      // ignore QR draw failure
    }
  }

  const safeName = fullName.replace(/[\\/:*?"<>|]/g, '-').trim() || 'employee'
  const filename = `ID-${idCode}-${safeName}.pdf`
  doc.save(filename)
}

async function onPrintId() {
  if (!canPrintEmployee.value) return
  const employee = activeMenuUser.value
  openActionMenuId.value = null
  menuTriggerEl = null
  if (!employee) return

  const fullName = String(employee.full_name || '').trim() || 'Employee'
  if (!confirm(`Are you sure to print id for employee ${fullName}`)) return

  try {
    await generateEmployeeIdCardPdf(employee)
  } catch (err) {
    console.error('Error printing employee ID card:', err)
    alert('Failed to print employee ID card.')
  }
}

async function findEmployees() {
  if (!canFindEmployee.value) return
  errorMessage.value = ''
  const name = String(filters.value.name || '').trim()
  const id = String(filters.value.id || '').trim()
  const phone = String(filters.value.phone || '').trim()

  if (!name && !id && !phone) {
    errorMessage.value = 'Enter an ID, name, and/or phone to find an employee.'
    return
  }

  loading.value = true
  hasSearched.value = true
  openActionMenuId.value = null
  menuTriggerEl = null

  try {
    const params = new URLSearchParams()
    if (id) params.set('id', id)
    if (name) params.set('name', name)
    if (phone) params.set('phone', phone)

    const response = await apiFetch(`/users/find-employee?${params.toString()}`)
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to find employees')
    }

    const data = await response.json().catch(() => [])
    users.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Error finding employees:', err)
    users.value = []
    errorMessage.value = err.message || 'Failed to find employees'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  window.addEventListener('click', closeActionMenu)
  window.addEventListener('scroll', handleMenuReposition, true)
  window.addEventListener('resize', handleMenuReposition)
})

onUnmounted(() => {
  window.removeEventListener('click', closeActionMenu)
  window.removeEventListener('scroll', handleMenuReposition, true)
  window.removeEventListener('resize', handleMenuReposition)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}

th, td {
  padding: 3px 15px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}

th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f8fafc;
}

.status-pill {
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: bold;
}

.active { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: none;
}

.actions button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions .menu-btn {
  color: #64748b;
  text-decoration: none;
}

.actions .menu-btn:hover {
  color: #0d4169;
}
</style>

<style>
.action-menu {
  position: fixed;
  z-index: 250;
  min-width: 11rem;
  overflow: auto;
}

.employee-action-menu-panel {
  width: 11rem;
}

.employee-action-menu-panel .action-menu-item {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e293b;
  text-align: left;
  white-space: nowrap;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;
  border-radius: 0.375rem;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.employee-action-menu-panel .action-menu-item:last-child {
  border-bottom: none;
}

.employee-action-menu-panel .action-menu-item:hover {
  background: #f8fafc;
}

.employee-action-menu-panel .action-menu-item-edit:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

@keyframes action-menu-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-menu-animate {
  animation: action-menu-in 0.2s ease-out forwards;
}
</style>
